import { toast } from "react-toastify";
import { API } from "../config/constants";

/**
 * Returns date in DDMMYY format
 * Accepts Date | string | timestamp
 */
const getDDMMYY = (input) => {
  const date = input instanceof Date ? input : new Date(input);

  // safety check
  if (isNaN(date.getTime())) {
    console.error("Invalid date passed to getDDMMYY:", input);
    return { dd: "00", mm: "00", yy: "00" };
  }

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);

  return { dd, mm, yy };
};

export function normalizeDateFormat(date) {
  if (!date) return "";

  // If already in DD-MM-YYYY, return as is
  if (/^\d{2}-\d{2}-\d{4}$/.test(date)) return date;

  // If in DD/MM/YYYY, convert to DD-MM-YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    return date.replaceAll("/", "-");
  }

  return date; // fallback (in case other format comes)
};

const generateRefId = (suffix, lastNumber = 0, createdAt) => {
  const { dd, mm, yy } = getDDMMYY(createdAt);
  const seq = String(lastNumber).padStart(3, "0");

  return suffix
    ? `${dd}${mm}${yy}-${suffix}${seq}`
    : `${dd}${mm}${yy}-${seq}`;
};

export const generateSketchRefId = (lastNumber, createdAt) =>
  generateRefId("S", lastNumber, createdAt);



/**
 * Claim / Case reference ID → DDMMYY-001
 */
export const generateCaseRefId = (lastNumber, createdAt) =>
  generateRefId("", lastNumber, createdAt);



export function formatDateToDMY(dateString) {
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}

export function formatDashboardDateTime(dateString) {
  if (!dateString) return "-";

  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  const hours = String(date.getHours()).padStart(2, "0"); // 24-hour format
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
}



export function urlDateTimeFromCreatedAt(createdAt) {
  if (!createdAt) return null;

  const d = new Date(createdAt);

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);

  const hours = String(d.getHours()).padStart(2, "0"); // 24-hour format
  const minutes = String(d.getMinutes()).padStart(2, "0");

  // const day = String(d.getUTCDate()).padStart(2, "0");
  // const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  // const year = String(d.getUTCFullYear()).slice(-2);

  // const hours = String(d.getUTCHours()).padStart(2, "0");
  // const minutes = String(d.getUTCMinutes()).padStart(2, "0");

  return `${day}${month}${year}-${hours}${minutes}`; // 161025-1020
}


export function urlDateToDDMMYY(date) {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);

  return `${day}${month}${year}`; // 020125
};

export function urlTimeToHHMM(time, period) {
  if (!time) return null;

  let [hours, minutes] = time.split(":").map(Number);

  if (period?.toLowerCase() === "pm" && hours !== 12) {
    hours += 12;
  }

  if (period?.toLowerCase() === "am" && hours === 12) {
    hours = 0;
  }

  return `${hours.toString().padStart(2, "0")}${minutes
    .toString()
    .padStart(2, "0")}`;
}



export function formatDateToYMD(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
}

export function formatTime12Hour(dateString) {
  const date = new Date(dateString);

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;

  return `${hours}:${minutes} ${ampm}`;
}

export function formatEventTime12Hour(timeString) {
  if (!timeString) return "-";

  const [hoursStr, minutesStr] = timeString.split(":");
  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr;

  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;

  return `${hours}:${minutes} ${ampm}`;
}


export function formatId(id) {
  const num = Number(id);
  if (isNaN(num)) return "000000";
  return num.toString().padStart(6, "0");
}

/**
 * Dynamically format all ID fields in an object
 * Any property ending with "Id" (case-insensitive) will be formatted
 */
export function formatObjectIds(obj) {
  const newObj = { ...obj };
  Object.keys(newObj).forEach((key) => {
    if (/id$/i.test(key) && newObj[key] !== undefined && newObj[key] !== null) {
      newObj[key] = formatId(newObj[key]);
    }
  });
  return newObj;
}

// current date
export const getTodayFormatted = () => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};


/**
 * Format time string to "HH:MM AM/PM"
 * @param {string} timeString - in "HH:MM:SS" format
 * @param {string} period - "AM" or "PM"
 * @returns {string} formatted time or "N/A"
 */
export function formatTimeHM(timeString, period) {
  if (!timeString) return "N/A";

  // Extract hours and minutes only
  const hhmm = timeString.slice(0, 5);

  return `${hhmm} ${period || ""}`.trim();
}

// trim model name
export const trimMakeFromModelLabel = (label = "") => {
  if (!label) return "";

  // remove first word + space (Make name)
  return label.replace(/^[^\s]+\s/, "");
};

// parse documents - step1 claim
export function parseFile(fileStr) {
  if (!fileStr) return null;

  // If already object (from upload preview)
  if (typeof fileStr === "object" && fileStr.url) {
    return fileStr;
  }

  // If plain string path (NEW FORMAT)
  if (typeof fileStr === "string" && !fileStr.trim().startsWith("[")) {
    return {
      name: fileStr.split("/").pop(),
      url: fileStr
    };
  }

  // If old JSON array string
  try {
    const parsed = JSON.parse(fileStr);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return {
        name: parsed[0].split("/").pop(),
        url: parsed[0]
      };
    }
  } catch {
    return null;
  }

  return null;
};

// file preview

export function handlePreview(file) {
  if (!file) return;

  let href;

  const normalizeFilePath = (file) => {
    if (!file) return null;

    // 1️⃣ If array
    if (Array.isArray(file)) {
      return file[0];
    }

    // 2️⃣ If object with url
    if (file?.url) {
      return file.url;
    }

    // 3️⃣ If object with path (YOUR CASE)
    if (file?.path) {
      return file.path;
    }

    // 4️⃣ Blob or File
    if (file instanceof File || file instanceof Blob) {
      return URL.createObjectURL(file);
    }

    // 5️⃣ JSON string array
    if (typeof file === "string") {
      try {
        const parsed = JSON.parse(file);
        if (Array.isArray(parsed)) {
          return parsed[0];
        }
      } catch {
        // not JSON
      }
      return file;
    }

    return null;
  };

  const filePath = normalizeFilePath(file);

  if (!filePath) return;

  // Blob URLs
  if (filePath.startsWith("blob:")) {
    href = filePath;
  }
  // Absolute URLs
  else if (filePath.startsWith("http")) {
    href = filePath;
  }
  // Backend relative path
  else {
    href = `${API.IMAGE_BASE_URL}/${filePath}`;
  }

  window.open(href, "_blank");
};

export async function handlePreviewModal(file, setPdfUrl, setPreviewOpen, setLoadingPdf) {
  if (!file) {
    toast.error("No rental document available");
    return;
  }

  const normalizeFilePath = (file) => {
    if (!file) return null;
    if (Array.isArray(file)) return file[0];
    if (file?.url) return file.url;
    if (file?.path) return file.path;
    if (file instanceof File || file instanceof Blob) return URL.createObjectURL(file);
    if (typeof file === "string") {
      try {
        const parsed = JSON.parse(file);
        if (Array.isArray(parsed)) return parsed[0];
      } catch { }
      return file;
    }
    return null;
  };

  const filePath = normalizeFilePath(file);
  if (!filePath) {
    toast.error("Unable to preview document");
    return;
  }

  let href = filePath.startsWith("blob:") || filePath.startsWith("http")
    ? filePath
    : `${API.IMAGE_BASE_URL}/${filePath}`;

  try {
    setLoadingPdf(true);

    const response = await fetch(href);
    if (!response.ok) throw new Error("Network response was not ok");

    const blob = await response.blob();
    const fileURL = URL.createObjectURL(blob);

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      window.open(fileURL, "_blank");
    } else {
      setPdfUrl(fileURL);
      setPreviewOpen(true);
    }
  } catch (err) {
    console.error("Failed to preview PDF:", err);
    toast.error("Failed to preview document");
  } finally {
    setLoadingPdf(false);
  }
}

//percentage color
export const getProgressColor = (percentage = 0) => {
  if (percentage >= 90) return '#16a34a';
  if (percentage >= 80) return '#22c55e';
  if (percentage >= 70) return '#84cc16';
  if (percentage >= 60) return '#eab308';
  if (percentage >= 50) return '#facc15';
  if (percentage >= 40) return '#fb923c';
  if (percentage >= 30) return '#f97316';
  if (percentage >= 20) return '#ef4444';
  if (percentage >= 10) return '#dc2626';
  return '#9ca3af';
};

export const formatCurrency = (amount, currency = 'USD') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// for claim detail validation 
export const isSameDate = (date1, date2) => {
  if (!date1 || !date2) return false;

  const parseToYMD = (dateStr) => {
    const nums = dateStr.match(/\d+/g);
    if (!nums || nums.length < 3) return [];

    // Case 1: YYYY-MM-DD
    if (nums[0].length === 4) {
      return [{
        year: nums[0],
        month: nums[1].padStart(2, "0"),
        day: nums[2].padStart(2, "0"),
      }];
    }

    // Case 2: XX/XX/YYYY → could be DD/MM or MM/DD
    if (nums[2].length === 4) {
      const year = nums[2];

      const option1 = {
        year,
        month: nums[1].padStart(2, "0"), // DD/MM/YYYY
        day: nums[0].padStart(2, "0"),
      };

      const option2 = {
        year,
        month: nums[0].padStart(2, "0"), // MM/DD/YYYY
        day: nums[1].padStart(2, "0"),
      };

      return [option1, option2];
    }

    return [];
  };

  const dates1 = parseToYMD(date1);
  const dates2 = parseToYMD(date2);

  for (const d1 of dates1) {
    for (const d2 of dates2) {
      if (
        d1.year === d2.year &&
        d1.month === d2.month &&
        d1.day === d2.day
      ) {
        return true;
      }
    }
  }

  return false;
};

// fomat local data => global filters
export const formatLocalDate = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};


// utils/formatEventType.js
export const formatEventType = (str) => {
  if (!str) return "";
  return str
    .split("_")                      // split snake_case
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize
    .join(" ");                       // join with spaces
};